import classNames from 'clsx';
import React from 'react';
import { useField } from 'react-final-form';

import useWizardContext from '@swf/core/hooks/useWizardContext';

import { CardType } from '../../../../../../types/cardType';
import {
  convertAmountByRate,
  getConversionRate,
  trimWithoutRounding
} from '../../../../../../utils/cashier';
import { calculateFee, hasFee } from '../../../../../../utils/fee';

import {
  TXTWCL_Commission,
  TXTWCL_Exchange_rate,
  TXTWCL_Total_to_pay,
  TXTWCL_Total_you_receive
} from './AmountSummary.messages.json';
import { bottom, delimeter, pay, total, wrapper } from './AmountSummary.scss';
import AmountSummaryRecord from './components/AmountSummaryRecord/AmountSummaryRecord';
import PayeeRecord from './components/PayeeRecord/PayeeRecord';

const AmountSummary: React.FC = () => {
  const {
    data: {
      paySystemDetails: {
        rates: { curr: balanceCurrency, rates },
        paysysFee: paySystemFee,
        type
      }
    }
  } = useWizardContext();

  const {
    input: {
      value: { amount, currency: selectedCurrency }
    }
  } = useField('deposit', { subscription: { value: true } });

  const conversionRate = getConversionRate(rates, selectedCurrency);
  const showOnlyTotalToPayRecord = [
    CardType.PsPayAstropay,
    CardType.BoletoAstropay,
    CardType.PsPayInovapay
  ].includes(type);

  const showFeeRecord =
    !showOnlyTotalToPayRecord && paySystemFee.paysysFeeApplies && hasFee(amount, paySystemFee);
  const fee = showFeeRecord
    ? trimWithoutRounding(
        calculateFee(
          convertAmountByRate(amount, conversionRate, balanceCurrency, false),
          paySystemFee
        )
      )
    : 0;
  const showConversionRecord = !showOnlyTotalToPayRecord && selectedCurrency !== balanceCurrency;
  const showTotalToPayRecord = showOnlyTotalToPayRecord || showConversionRecord || showFeeRecord;
  const showPayeeRecord = type === CardType.OpenBanking;

  return (
    <div className={wrapper}>
      {showPayeeRecord && <PayeeRecord />}
      {showTotalToPayRecord && (
        <AmountSummaryRecord
          amount={amount}
          className={pay}
          currency={selectedCurrency}
          label={TXTWCL_Total_to_pay}
          variant="pay"
          isSingleRecord={showOnlyTotalToPayRecord}
          isPayeeRecordShown={showPayeeRecord}
        />
      )}
      {showPayeeRecord && <div className={delimeter} />}
      {showConversionRecord && (
        <AmountSummaryRecord
          amount={amount}
          currency={balanceCurrency}
          selectedCurrency={selectedCurrency}
          label={TXTWCL_Exchange_rate}
          rate={conversionRate}
          variant="rate"
        />
      )}
      {showFeeRecord && (
        <AmountSummaryRecord
          amount={fee}
          currency={balanceCurrency}
          label={TXTWCL_Commission}
          variant="commission"
        />
      )}
      {!showOnlyTotalToPayRecord && (
        <AmountSummaryRecord
          amount={amount}
          className={classNames(total, showTotalToPayRecord && bottom)}
          currency={balanceCurrency}
          fee={fee}
          label={TXTWCL_Total_you_receive}
          rate={conversionRate}
          variant="total"
        />
      )}
    </div>
  );
};

export default AmountSummary;
